import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import useAuthStore from '../../stores/authStore.js';
import useCartStore from '../../stores/cartStore.js';
import InstallAppButton from '../InstallAppButton.jsx';

const WA = 'https://wa.me/919700104108';
const TEL = 'tel:+919700104108';

const DRAWER_LINKS = [
  { to: '/', label: 'Home', icon: '🏠' },
  { to: '/diagnostics', label: 'Diagnostics', icon: '🔬' },
  { to: '/diagnostics?filter=popular', label: 'Popular tests', icon: '⭐', indent: true },
  { to: '/health-concerns', label: 'Health Concerns', icon: '💚' },
  { to: '/upload-prescription', label: 'Upload Prescription', icon: '📋' },
  { to: '/my-orders', label: 'My Bookings', icon: '📅', auth: true },
  { to: '/reports', label: 'Reports', icon: '📄', auth: true },
  { to: '/checkout', label: 'Cart', icon: '🛒', cart: true },
];

export default function Header() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const cartCount = useCartStore((s) => s.count());
  const cartOpen = useCartStore((s) => s.drawerOpen);
  const [menuOpen, setMenuOpen] = useState(false);

  const closeMenu = () => setMenuOpen(false);

  useEffect(() => {
    const lock = menuOpen || cartOpen;
    document.body.style.overflow = lock ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen, cartOpen]);

  const onSearch = (e) => {
    e.preventDefault();
    const q = new FormData(e.currentTarget).get('q');
    navigate(q ? `/diagnostics?q=${encodeURIComponent(String(q))}` : '/diagnostics');
  };

  return (
    <header className="site-header">
      <div className="header-row">
        <div className="header-left">
          <button
            type="button"
            className="hdr-icon-btn menu"
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((v) => !v)}
          >
            {menuOpen ? (
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            ) : (
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </svg>
            )}
          </button>
        </div>

        <Link to="/" className="header-logo" aria-label="Jeevan HealthCare home" onClick={closeMenu}>
          <img src="/logo.png" alt="Jeevan HealthCare at Home" />
        </Link>

        <div className="header-right">
          <InstallAppButton variant="header" />
          <button
            type="button"
            className="hdr-icon-btn cart"
            aria-label="Cart"
            title="Cart"
            onClick={() => useCartStore.getState().setDrawerOpen(true)}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
              <circle cx="9" cy="21" r="1" />
              <circle cx="20" cy="21" r="1" />
              <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
            </svg>
            {cartCount > 0 ? <span className="hdr-cart-badge">{cartCount}</span> : null}
          </button>
          <a
            href={WA}
            className="hdr-icon-btn wa"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="WhatsApp"
            title="WhatsApp"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z" />
            </svg>
          </a>

          {user ? (
            <Link to="/dashboard" className="hdr-user-chip" title="My Health">
              {user.name?.split(' ')[0] || user.phone || 'Account'}
            </Link>
          ) : (
            <Link to="/signup" className="btn btn-primary hdr-login">
              Login
            </Link>
          )}
        </div>
      </div>

      <div className="header-search-bar">
        <form onSubmit={onSearch}>
          <label className="sr-only" htmlFor="global-search">
            Search tests
          </label>
          <input
            id="global-search"
            name="q"
            type="search"
            placeholder="Search test, package, disease, symptoms…"
            autoComplete="off"
            enterKeyHint="search"
          />
        </form>
      </div>

      {/* Desktop secondary nav — IA labels */}
      <nav className="header-ia-nav" aria-label="Site sections">
        <Link to="/">Home</Link>
        <Link to="/diagnostics">Diagnostics</Link>
        <Link to="/health-concerns">Health Concerns</Link>
        <Link to="/upload-prescription">Upload Prescription</Link>
        {user ? (
          <>
            <Link to="/my-orders">My Bookings</Link>
            <Link to="/reports">Reports</Link>
          </>
        ) : (
          <Link to="/signup">Login</Link>
        )}
      </nav>

      {menuOpen && (
        <>
          <button type="button" className="nav-drawer-backdrop" aria-label="Close menu" onClick={closeMenu} />
          <nav className="nav-drawer" aria-label="Main menu">
            <img src="/logo.png" alt="" />
            {DRAWER_LINKS.map((item) => {
              if (item.auth && !user) return null;
              const label =
                item.cart && cartCount > 0 ? `${item.label} (${cartCount})` : item.label;
              return (
                <Link
                  key={item.to + item.label}
                  to={item.to}
                  onClick={closeMenu}
                  className={item.indent ? 'nav-drawer-indent' : undefined}
                >
                  {item.icon} {label}
                </Link>
              );
            })}
            {!user && (
              <Link to="/my-orders" onClick={closeMenu}>
                📅 My Bookings
              </Link>
            )}
            {!user && (
              <Link to="/reports" onClick={closeMenu}>
                📄 Reports
              </Link>
            )}
            <div className="drawer-install" onClick={closeMenu}>
              <InstallAppButton variant="block" />
            </div>
            <Link to="/onboarding-phlebotomist" onClick={closeMenu}>
              💉 Join as phlebotomist
            </Link>
            <Link to={user ? '/dashboard' : '/signup'} onClick={closeMenu}>
              👤 {user ? 'My Health' : 'Login / Sign up'}
            </Link>
            <a href={TEL} onClick={closeMenu}>
              📞 Call us
            </a>
            <a href={WA} target="_blank" rel="noopener noreferrer" onClick={closeMenu}>
              💬 WhatsApp
            </a>
            {user && (
              <button
                type="button"
                className="linkish"
                onClick={() => {
                  closeMenu();
                  logout();
                }}
              >
                🚪 Log out
              </button>
            )}
            <div className="drawer-meta">
              Jeevan HealthCare at Home
              <br />
              ISO 9001:2015 · Hyderabad
            </div>
          </nav>
        </>
      )}
    </header>
  );
}
